import { useEffect, useState } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { PageModeToggle } from "../shared/PageModeToggle";
import type { PageMode } from "@/hooks/usePageMode";

// Sprint 8.1 — Header de /appointments conforme cal-direction1.jsx PageHead.
// H1 dynamique (mois courant) + sous-titre semaine + segmented Mois/Semaine/
// Jour + bouton Nouveau RDV.
//
// Le segmented dispatch un CustomEvent "atomic-calendar:set-view" écouté
// par AppointmentList qui pilote Schedule-X via calendar.calendarControls.

export type CalendarView = "month-grid" | "week" | "day";

const VIEW_LABELS: Record<CalendarView, string> = {
  "month-grid": "Mois",
  week: "Semaine",
  day: "Jour",
};

const MONTH_NAMES_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

function getCurrentWeekInfo(): { weekNumber: number; range: string } {
  const now = new Date();
  // ISO week : jeudi de la semaine définit l'année
  const target = new Date(now.valueOf());
  const dayNr = (now.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const weekNumber =
    1 +
    Math.round(
      ((target.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getDay() + 6) % 7)) /
        7,
    );

  // Range Lun-Ven de la semaine courante
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayNr);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const fmt = (d: Date) =>
    d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  const range = `${monday.getDate()} – ${fmt(friday)}`;
  return { weekNumber, range };
}

interface AppointmentPageHeadProps {
  mode: PageMode;
  onToggleMode: () => void;
  view: CalendarView;
  onViewChange: (v: CalendarView) => void;
  onCreate: () => void;
}

export function AppointmentPageHead({
  mode,
  onToggleMode,
  view,
  onViewChange,
  onCreate,
}: AppointmentPageHeadProps) {
  const [now, setNow] = useState(() => new Date());

  // Refresh "now" every minute pour titre/semaine cohérents
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const monthLabel = `${MONTH_NAMES_FR[now.getMonth()]} ${now.getFullYear()}`;
  const { weekNumber, range } = getCurrentWeekInfo();
  const subtitle = `Semaine ${weekNumber} · ${range}`;

  const handleViewChange = (v: CalendarView) => {
    onViewChange(v);
    // Dispatch CustomEvent — Schedule-X écouté côté AppointmentCalendarView
    window.dispatchEvent(
      new CustomEvent("atomic-calendar:set-view", { detail: { view: v } }),
    );
  };

  const handleNav = (direction: "prev" | "today" | "next") => {
    window.dispatchEvent(
      new CustomEvent("atomic-calendar:nav", { detail: { direction } }),
    );
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3 min-w-0">
        <CalendarIcon className="size-5 text-violet-700 dark:text-violet-300 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold tracking-tight truncate leading-tight">
            {monthLabel}
          </h1>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {/* Nav prev/today/next */}
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleNav("prev")}
            className="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60"
            aria-label="Précédent"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => handleNav("today")}
            className="h-8 px-3 rounded-md text-[12.5px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60"
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={() => handleNav("next")}
            className="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60"
            aria-label="Suivant"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        {/* Segmented view */}
        <div className="inline-flex items-center rounded-md border border-border p-0.5 bg-card">
          {(Object.keys(VIEW_LABELS) as CalendarView[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleViewChange(v)}
              className={cn(
                "h-7 px-2.5 rounded text-[12px] font-medium transition-colors",
                view === v
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={view === v}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>

        {/* Nouveau RDV primary */}
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-medium text-white"
          style={{ background: "var(--accent-solid)" }}
        >
          <Plus className="size-3.5" />
          Nouveau RDV
        </button>

        <PageModeToggle mode={mode} onToggle={onToggleMode} />
      </div>
    </div>
  );
}
